import Router, {useRouter} from 'next/router'
import {memo, useMemo} from 'react'

import {IconArrowRightBold} from '@defign/asset'
import {Action, sendAULog} from '@financial/common-logs'
import {CONNECT_SCHEME} from '@mydata/utils/connect'
import classNames from 'classnames/bind'

import LayerTitle from '$components/common/layer/LayerTitle'
import {QRType} from '$constants/insurance/qrBridgeTitle'
import {INTERNAL_ROUTES} from '$constants/internal-routes'
import {useNewConnectionUrl} from '$hooks/useConnectUrl'
import buildUrl from '$utils/buildUrl'
import {sendClickNlog} from '$utils/nlog'

import Styles from './NotDisplayedInsurancesLayer.module.scss'

import type {MouseEvent} from 'react'

const cx = classNames.bind(Styles)

function NotDisplayedInsurancesLayer({
    hashId,
    isValidApp,
    onClose,
}: {
    hashId: string
    isValidApp: boolean
    onClose: () => void
}) {
    const router = useRouter()

    const {rurl, curl} = useMemo(
        () => ({
            rurl: buildUrl({pathname: window.location.pathname, query: Router.query}),
            curl: buildUrl({pathname: window.location.pathname, query: Router.query, hash: hashId}),
        }),
        [hashId],
    )

    const {enterConnectUrl} = useNewConnectionUrl({scheme: CONNECT_SCHEME.MYDATA_INSURANCE, rurl, curl})

    async function handleConnect(e: MouseEvent<HTMLAnchorElement>) {
        e.preventDefault()
        sendClickNlog('insumy.nocheck')

        sendAULog(
            {
                service: 'md',
                area: 'B',
                page: 'B',
                code: 'click_missing_insu',
            },
            Action.Click,
        )

        onClose()

        if (!isValidApp) {
            router.replace(
                {
                    pathname: router.pathname,
                    query: {
                        ...router.query,
                        showAppDownloadQR: true,
                        appBridgeUrl: buildUrl({
                            pathname: INTERNAL_ROUTES.INSURANCE_MY_HOME,
                            withBasePath: true,
                        }),
                        qrType: QRType.INSU,
                    },
                },
                undefined,
                {shallow: true},
            )
            return
        }

        enterConnectUrl()
    }

    return (
        <>
            <LayerTitle title="조회되지 않는 보험이 있나요?" />
            <p className={cx('notice')}>내가 가입한 보험이 전부 조회되지 않을 수 있습니다.</p>
            <div className={cx('article')}>
                <div className={cx('scroll-area')}>
                    <div className={cx('check-wrap')}>
                        <h4 className={cx('title')}>연결되지 않은 보험사가 있는지 확인해보세요</h4>
                        <p className={cx('description')}>
                            마이데이터는 보험사별로 등록이 이뤄집니다. 혹시 연결되지 않은 보험사가 있는지 확인해 보세요.
                        </p>
                        <div className={cx('link-area')}>
                            <a className={cx('link')} onClick={handleConnect}>
                                내 보험 연결 확인하기
                                <IconArrowRightBold width={6} height={10} fill="#09aa5c" />
                            </a>
                        </div>
                        <h4 className={cx('title')}>물건이나 재산에 발생된 피해를 보상하는 보험도 조회돼요</h4>
                        <p className={cx('description')}>
                            기존에는 ‘장기인보험’ 즉 사람의 생명이나 신체에 생긴 손해를 보장하는 장기간의 보험만
                            조회되었으나, 마이데이터 정보 제공 기준에 따라 ‘단기인보험’ 뿐만 아니라 ‘물/일반보험’ 즉
                            물건과 기타 재산에 발생한 피해를 보상하는 보험도 조회됩니다.
                        </p>
                        <div className={cx('box')}>
                            <dl className={cx('example', 'type-half')}>
                                <dt className={cx('key')}>조회 가능한 물・일반보험 예시</dt>
                                <dd className={cx('value')}>화재/재물 보험</dd>
                                <dd className={cx('value')}>골프보험</dd>
                                <dd className={cx('value')}>여행자보험</dd>
                                <dd className={cx('value')}>배상책임보험</dd>
                                <dd className={cx('value')}>펫보험</dd>
                                <dd className={cx('value')}>보증(신용)보험</dd>
                            </dl>
                        </div>
                        <h4 className={cx('title')}>‘보장’ 정보는 2000년 이후 계약건만 조회돼요</h4>
                        <ul className={cx('description')}>
                            <li>생명보험 : 2000년 4월 이후 계약</li>
                            <li>손해보험 : 2002년 12월 이후 계약</li>
                        </ul>
                        <h4 className={cx('title')}>보장이 끝났다면 최대 5년 까지만 조회돼요</h4>
                        <p className={cx('description')}>만기 된 보험은 만기일 기준 5년까지만 조회됩니다.</p>
                    </div>
                    <div className={cx('provide-area')}>
                        네이버 파이낸셜은 마이데이터 사업자로서 각 금융회사로부터 제공받은 정보를 기준으로 서비스를
                        제공합니다. 실제 가입한 보험의 정보와 다를 수 있으며, 자세한 내용은 가입한 보험상품의 증권과
                        약관을 확인해 주세요.
                    </div>
                </div>
            </div>
        </>
    )
}

export default memo(NotDisplayedInsurancesLayer)
